/**
 * API client for Resume Screener backend.
 * Provides configured axios instance and typed API methods.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_VERSION = '/api/v1';

/**
 * Configured axios instance with interceptors
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  timeout: 30000,
  // Don't set default Content-Type - let axios handle it per request
});

/**
 * Request interceptor to add authentication token
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle token refresh
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If unauthorized and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');

          // Check if there's a critical operation in progress
          const criticalOp = localStorage.getItem('critical_operation_in_progress');
          if (criticalOp) {
            // Don't redirect during critical operations - let the error propagate
            console.warn('Auth failed during critical operation - not redirecting');
            return Promise.reject(error);
          }

          window.location.href = '/login';
          return Promise.reject(error);
        }

        const response = await axios.post(`${API_BASE_URL}${API_VERSION}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Update stored tokens
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', newRefreshToken);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');

        // Check if there's a critical operation in progress
        const criticalOp = localStorage.getItem('critical_operation_in_progress');
        if (criticalOp) {
          // Don't redirect during critical operations - let the error propagate
          console.warn('Token refresh failed during critical operation - not redirecting');
          return Promise.reject(refreshError);
        }

        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Type definitions for API responses
 */

// Authentication
export interface LoginResponse {
  authorization_url: string;
  state: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserResponse {
  id: string;
  email: string;
  name?: string;
  picture?: string;
  email_verified: boolean;
}

/**
 * API methods
 */

// Authentication API
export const authAPI = {
  getLoginUrl: (provider: string = 'google') =>
    apiClient.get<LoginResponse>(`/auth/login`, { params: { provider } }),

  demoLogin: (email: string = 'demo@example.com', name: string = 'Demo User') =>
    apiClient.post<TokenResponse>(`/demo/login`, { email, name }),

  refreshToken: (refreshToken: string) =>
    apiClient.post<TokenResponse>(`/auth/refresh`, { refresh_token: refreshToken }),

  getCurrentUser: () =>
    apiClient.get<UserResponse>(`/auth/me`),

  logout: () =>
    apiClient.post(`/auth/logout`),
};

// Binder Processing API
export interface ProcessBinderResponse {
  task_id: string;
  status: string;
  message: string;
  created_at: string;
}

export interface ProcessingStatusResponse {
  task_id: string;
  status: string;
  phase: string | null;
  percent_complete: number;
  resumes_detected: number;
  resumes_processed: number;
  total_resumes: number;
  evaluations_in_db: number;  // Actual count of evaluations created
  is_incomplete: boolean;  // True if some resumes failed to process
  is_stuck: boolean;  // True if task hasn't updated in >10 minutes
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  csv_available: boolean;
}

export const binderAPI = {
  processBinder: (sessionId: string, file: File, criteria: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('screening_session_id', sessionId);
    formData.append('criteria_text', criteria);
    // Use longer timeout for file upload (large binders need S3 upload + splitting)
    return apiClient.post<ProcessBinderResponse>(`/screening/process-binder`, formData, {
      timeout: 600000, // 10 minutes for large file uploads
    });
  },

  getProcessingStatus: (taskId: string) =>
    apiClient.get<ProcessingStatusResponse>(`/screening/process-status/${taskId}`),

  retryFailedTask: (taskId: string) =>
    apiClient.post(`/screening/retry-failed/${taskId}`, null, {
      timeout: 120000, // 2 minutes (may download binder from S3 and re-split)
    }),
};

// ============================================================================
// Role-Based Architecture APIs (New)
// ============================================================================

// Role types
export interface Role {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  criteria?: string;  // Optional for draft roles
  is_criteria_locked: boolean;
  locked_at?: string;
  status: 'active' | 'draft' | 'paused' | 'closed';
  total_evaluations: number;
  last_evaluated_at?: string;
  created_at: string;
  updated_at: string;
  is_processing?: boolean;  // True if there's an active background processing task
  failed_task_id?: string;  // ID of failed processing task
  processing_error?: string;  // Error message from failed task
  failed_phase?: string;  // Phase where processing failed
  resumes_detected?: number;  // Total resumes detected in binder
  is_incomplete?: boolean;  // True if some resumes failed to process
  is_stuck?: boolean;  // True if processing hasn't updated in >10 minutes
}

export interface CandidateEvaluation {
  rank: number; // Dynamic - calculated on-the-fly
  id: string;
  resume_id: string;
  full_name?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  score: number;
  letter_grade: string;
  requires_manual_review: boolean;
  qualitative_summary?: string;
  evaluation_status: string;
  evaluated_at?: string;
}

export interface ResumeBank {
  id: string;
  original_filename?: string;
  extracted_name?: string;
  file_size?: number;
  page_count: number;
  upload_user_id?: string;
  status: string;
  created_at: string;
  evaluation_count: number;
}

// Criteria Chatbot types
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  message: string;
  suggestions?: string[];
  parsed_criteria?: any;
  is_complete: boolean;
  confidence?: string;
}

export interface CriteriaValidationResponse {
  is_valid: boolean;
  issues: string[];
  suggestions: string[];
  ambiguities: string[];
  parsed_preview?: any;
}

// Conversation types
export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface Conversation {
  role_id: string;
  conversation_history: ConversationMessage[];
  updated_at: string;
}

// Role API
export const roleAPI = {
  // Role management
  createRole: (data: { title: string; description?: string; criteria?: string; status?: 'draft' | 'active' }) =>
    apiClient.post<Role>(`/roles`, data, {
      timeout: 60000, // 60 seconds for criteria parsing and validation
    }),

  listRoles: (status?: 'active' | 'paused' | 'closed' | 'draft') =>
    apiClient.get<Role[]>(`/roles`, { params: { status } }),

  getRole: (roleId: string) =>
    apiClient.get<Role>(`/roles/${roleId}`),

  updateRole: (roleId: string, data: {
    title?: string;
    description?: string;
    criteria?: string;
    dimensions?: any[];
    flags?: any;
    thresholds?: any;
    status?: string
  }) =>
    apiClient.patch<Role>(`/roles/${roleId}`, data, {
      timeout: 60000, // May need to parse criteria
    }),

  deleteRole: (roleId: string) =>
    apiClient.delete(`/roles/${roleId}`),

  // Conversation management
  getConversation: (roleId: string) =>
    apiClient.get<Conversation>(`/roles/${roleId}/conversation`),

  updateConversation: (roleId: string, messages: ConversationMessage[]) =>
    apiClient.put<Conversation>(`/roles/${roleId}/conversation`, messages),

  // Binder upload for role (returns immediately with task_id for background processing)
  uploadBinderForRole: (roleId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/roles/${roleId}/upload-binder`, formData, {
      timeout: 600000, // 10 minutes (large binders: S3 upload + splitting before background processing starts)
    });
  },

  // Get active processing task for role (for reconnecting after timeout)
  getActiveTask: (roleId: string) =>
    apiClient.get<{ task_id: string; status: string; phase: string; resumes_detected: number; is_active: boolean }>(`/roles/${roleId}/active-task`),

  // Get candidates for role (with dynamic ranking)
  getCandidates: (roleId: string, params?: { limit?: number; offset?: number; min_score?: number; letter_grade?: string }) =>
    apiClient.get<CandidateEvaluation[]>(`/roles/${roleId}/candidates`, { params }),

  // Export results
  exportCSV: (roleId: string) =>
    apiClient.get(`/roles/${roleId}/export-csv`),

  downloadCSV: (roleId: string) =>
    apiClient.get(`/roles/${roleId}/download-csv`, { responseType: 'blob' }),

  // Delete candidates
  deleteAllCandidates: (roleId: string) =>
    apiClient.delete(`/roles/${roleId}/candidates`),

  deleteCandidate: (roleId: string, evaluationId: string) =>
    apiClient.delete(`/roles/${roleId}/candidates/${evaluationId}`),

  // Retry failed processing task
  retryFailedTask: (taskId: string) =>
    apiClient.post(`/screening/retry-failed/${taskId}`, null, {
      timeout: 120000, // 2 minutes (may download binder from S3 and re-split)
    }),

  // Clone role
  cloneRole: (roleId: string, data: { title: string; description?: string; criteria: string }) =>
    apiClient.post<Role>(`/roles/${roleId}/clone`, data, {
      timeout: 60000, // 60 seconds for criteria parsing
    }),
};

// Resume Bank API
export const resumeBankAPI = {
  // Search resume bank
  search: (query: string, params?: { limit?: number; offset?: number }) =>
    apiClient.get<ResumeBank[]>(`/resume-bank/search`, { params: { query, ...params } }),

  // Get resume details
  getResume: (resumeId: string) =>
    apiClient.get<ResumeBank>(`/resume-bank/${resumeId}`),

  // Download resume PDF (returns presigned URL for S3 or blob for local)
  downloadResume: (resumeId: string) =>
    apiClient.get(`/resume-bank/${resumeId}/download`),

  // Evaluate existing resume for role
  evaluateForRole: (resumeId: string, roleId: string) =>
    apiClient.post(`/resume-bank/${resumeId}/evaluate/${roleId}`),

  // Get all evaluations for resume
  getEvaluations: (resumeId: string) =>
    apiClient.get(`/resume-bank/${resumeId}/evaluations`),
};

// Criteria Chatbot API
export const criteriaChatAPI = {
  // Start conversation
  start: (roleTitle?: string) =>
    apiClient.post<ChatResponse>(`/criteria-chat/start`, { role_title: roleTitle }),

  // Continue conversation
  chat: (data: { conversation_history: ChatMessage[]; user_message: string; role_title?: string; role_id?: string }) =>
    apiClient.post<ChatResponse>(`/criteria-chat/chat`, data, {
      timeout: 180000, // 3 minutes for AI processing (finalization can take 1-2 minutes with long conversations)
    }),

  // Validate criteria
  validate: (criteriaText: string) =>
    apiClient.post<CriteriaValidationResponse>(`/criteria-chat/validate`, { criteria_text: criteriaText }, {
      timeout: 60000, // 60 seconds for validation processing
    }),
};

export default apiClient;
